import React from 'react';
import { CheckCircle, ArrowRight, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { productCategories } from '../data/mock';
import SEO from '../components/SEO';
import { seoMetadata, structuredData } from '../data/seo';

const Products = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title={seoMetadata.products.title}
        description={seoMetadata.products.description}
        keywords={seoMetadata.products.keywords}
        canonical={seoMetadata.products.canonical}
        additionalStructuredData={structuredData.products}
      />
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1590880265945-6b43effeb599)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
        
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Our Products</h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto text-gray-200 mb-4">
            Premium Quality Materials from Trusted Brands
          </p>
          <Link to="/ebook" className="inline-flex items-center text-red-400 hover:text-red-300 font-semibold group">
            <BookOpen className="w-5 h-5 mr-2" />
            Learn how to choose the right materials - Our Premium Homebuilding Guide
            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          {productCategories.map((category, index) => (
            <div
              key={category.id}
              id={category.slug}
              className={`mb-20 ${index !== 0 ? 'pt-20 border-t border-gray-200' : ''}`}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Image */}
                <div className={index % 2 === 0 ? 'order-1' : 'order-1 lg:order-2'}>
                  <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl group">
                    <img
                      src={category.image}
                      alt={category.name}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  </div>
                </div>

                {/* Content */}
                <div className={index % 2 === 0 ? 'order-2' : 'order-2 lg:order-1'}>
                  <div className="inline-block px-4 py-2 bg-red-100 text-red-600 rounded-full mb-6 font-medium">
                    Category {category.id}
                  </div>
                  
                  <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                    {category.name}
                  </h2>
                  
                  <p className="text-xl text-gray-600 mb-8">
                    {category.description}
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                    {category.products.map((product, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 font-medium">{product}</span>
                      </div>
                    ))}
                  </div>

                  <Link to="/contact">
                    <Button className="bg-red-600 hover:bg-red-700 text-white">
                      Get Price Quote
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Brands We Work With */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Trusted Brands
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We partner with India's leading manufacturers to bring you the best
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 max-w-6xl mx-auto">
            {[
              { name: 'Astral', logo: 'https://customer-assets.emergentagent.com/job_eb3e1544-7565-450f-a2a0-dd9eccd226a4/artifacts/jrcg2j9s_astral.jpeg' },
              { name: 'Birla Opus', logo: 'https://customer-assets.emergentagent.com/job_eb3e1544-7565-450f-a2a0-dd9eccd226a4/artifacts/y3182uhe_birla%20opus.png' },
              { name: 'Kajaria', logo: 'https://customer-assets.emergentagent.com/job_eb3e1544-7565-450f-a2a0-dd9eccd226a4/artifacts/ig62wpro_kajaria.jpeg' },
              { name: 'Roff', logo: 'https://customer-assets.emergentagent.com/job_eb3e1544-7565-450f-a2a0-dd9eccd226a4/artifacts/kau9bdd8_roff.jpg' },
              { name: 'Parryware', logo: 'https://customer-assets.emergentagent.com/job_eb3e1544-7565-450f-a2a0-dd9eccd226a4/artifacts/vq5ybrmy_parryware.jpg' },
              { name: 'Plumber', logo: 'https://customer-assets.emergentagent.com/job_eb3e1544-7565-450f-a2a0-dd9eccd226a4/artifacts/3465hl4h_plumber.png' }
            ].map((brand, index) => (
              <Card key={index} className="flex items-center justify-center p-4 border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 bg-white overflow-hidden">
                <CardContent className="p-0 w-full h-24 flex items-center justify-center">
                  <img 
                    src={brand.logo} 
                    alt={brand.name} 
                    className="max-h-20 max-w-full object-contain"
                  />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-red-600 via-red-700 to-red-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Need Help Choosing the Right Products?
          </h2>
          <p className="text-xl mb-8 text-red-100 max-w-2xl mx-auto">
            Get free expert consultation from our material specialist
          </p>
          
          <Link to="/contact">
            <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 shadow-xl">
              Contact Us Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Products;
